import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LayoutDashboard, FileText, Receipt, LifeBuoy, LogOut, Loader2,
  CheckCircle2, Circle, Send, Crown, CreditCard,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getDossiers, getInvoices, getTickets, createTicket, createInvoiceCheckout } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import AdminBackoffice from "@/pages/AdminBackoffice";

const TABS = [
  { key: "dossiers", label: "Mes dossiers", icon: FileText },
  { key: "factures", label: "Factures", icon: Receipt },
  { key: "support", label: "Support prioritaire", icon: LifeBuoy },
];

const statusColor = (s) => {
  const map = { "Payée": "text-emerald-600 bg-emerald-50", "En attente": "text-gold bg-gold/10",
    "En cours": "text-blue-700 bg-blue-50", "Diagnostic": "text-gold bg-gold/10", "Ouvert": "text-blue-700 bg-blue-50" };
  return map[s] || "text-anthracite bg-navy/5";
};

export default function EspaceClient() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dossiers");
  const [dossiers, setDossiers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState({ subject: "", message: "", priority: "haute" });
  const [sending, setSending] = useState(false);
  const [payingId, setPayingId] = useState(null);

  const payInvoice = async (invoiceId) => {
    setPayingId(invoiceId);
    try {
      const { checkout_url } = await createInvoiceCheckout(invoiceId);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error("Impossible d'initier le paiement. Réessayez.");
      setPayingId(null);
    }
  };

  const loadAll = () => {
    Promise.all([getDossiers(), getInvoices(), getTickets()])
      .then(([d, i, t]) => { setDossiers(d); setInvoices(i); setTickets(t); })
      .catch(() => toast.error("Impossible de charger vos données."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  const submitTicket = async (e) => {
    e.preventDefault();
    if (!ticket.subject || !ticket.message) return toast.error("Renseignez l'objet et le message.");
    setSending(true);
    try {
      const t = await createTicket(ticket);
      setTickets([t, ...tickets]);
      setTicket({ subject: "", message: "", priority: "haute" });
      toast.success("Demande envoyée. Notre équipe vous répond en priorité.");
    } catch { toast.error("Envoi impossible."); }
    finally { setSending(false); }
  };

  const outstanding = invoices.filter((i) => i.status !== "Payée").reduce((s, i) => s + i.amount, 0);

  if (user?.role === "admin") return <AdminBackoffice />;

  return (
    <section className="min-h-screen bg-cream pt-28 pb-24" data-testid="client-portal">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold"><LayoutDashboard size={14} /> Espace client</span>
            <h1 className="mt-2 font-display text-4xl text-navy">Bonjour, {user?.name?.split(" ")[0]} 👋</h1>
            <p className="mt-1 text-anthracite font-body">Suivez l'avancement de vos dossiers en temps réel.</p>
          </div>
          <button onClick={logout} data-testid="logout-button" className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-5 py-2.5 text-sm font-medium text-navy hover:bg-navy hover:text-white transition-colors">
            <LogOut size={15} /> Déconnexion
          </button>
        </div>

        {/* Summary cards */}
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {[
            { label: "Dossiers actifs", value: dossiers.length, icon: FileText },
            { label: "Factures en attente", value: invoices.filter((i) => i.status !== "Payée").length, icon: Receipt },
            { label: "Montant dû", value: `${outstanding.toLocaleString("fr-FR")} €`, icon: Crown },
          ].map((c) => (
            <div key={c.label} className="rounded-2xl border border-navy/10 bg-white p-6 flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-gold"><c.icon size={22} /></span>
              <div>
                <div className="font-display text-2xl text-navy">{c.value}</div>
                <p className="text-xs text-anthracite/70 uppercase tracking-wide">{c.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap gap-2 border-b border-navy/10">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`portal-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? "border-gold text-navy" : "border-transparent text-anthracite/60 hover:text-navy"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-gold" size={32} /></div>
        ) : (
          <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mt-8">
            {tab === "dossiers" && (
              <div className="grid gap-6 lg:grid-cols-2" data-testid="portal-dossiers">
                {dossiers.map((d) => (
                  <div key={d.id} className="rounded-2xl border border-navy/10 bg-white p-7">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <span className="text-xs text-anthracite/50 font-body">{d.ref} · {d.country}</span>
                        <h3 className="font-display text-xl text-navy leading-snug">{d.title}</h3>
                      </div>
                      <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${statusColor(d.status)}`}>{d.status}</span>
                    </div>
                    <div className="mt-5">
                      <div className="flex justify-between text-xs text-anthracite mb-1.5"><span>Avancement</span><span className="font-semibold text-navy">{d.progress}%</span></div>
                      <div className="h-2 rounded-full bg-navy/10 overflow-hidden">
                        <motion.div className="h-full rounded-full bg-gradient-to-r from-gold to-gold-light" initial={{ width: 0 }} animate={{ width: `${d.progress}%` }} transition={{ duration: 1, ease: "easeOut" }} />
                      </div>
                    </div>
                    <ul className="mt-5 space-y-2">
                      {d.steps?.map((s, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm font-body">
                          {s.done ? <CheckCircle2 size={16} className="text-gold" /> : <Circle size={16} className="text-navy/20" />}
                          <span className={s.done ? "text-navy" : "text-anthracite/50"}>{s.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {tab === "factures" && (
              <div className="rounded-2xl border border-navy/10 bg-white overflow-hidden" data-testid="portal-invoices">
                <table className="w-full text-left">
                  <thead className="bg-cream text-xs uppercase tracking-wide text-anthracite/60">
                    <tr><th className="px-6 py-4">N°</th><th className="px-6 py-4">Objet</th><th className="px-6 py-4">Montant</th><th className="px-6 py-4">Statut</th><th className="px-6 py-4 text-right">Action</th></tr>
                  </thead>
                  <tbody className="divide-y divide-navy/5 text-sm font-body">
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="hover:bg-cream/50 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                        <td className="px-6 py-4 font-medium text-navy">{inv.number}</td>
                        <td className="px-6 py-4 text-anthracite">{inv.label}</td>
                        <td className="px-6 py-4 font-semibold text-navy">{inv.amount.toLocaleString("fr-FR")} {inv.currency === "EUR" ? "€" : inv.currency}</td>
                        <td className="px-6 py-4"><span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusColor(inv.status)}`}>{inv.status}</span></td>
                        <td className="px-6 py-4 text-right">
                          {inv.status !== "Payée" ? (
                            <button
                              onClick={() => payInvoice(inv.id)}
                              disabled={payingId === inv.id}
                              data-testid={`pay-invoice-${inv.id}`}
                              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-4 py-2 text-xs font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                            >
                              {payingId === inv.id ? <Loader2 size={14} className="animate-spin" /> : <><CreditCard size={14} /> Payer en ligne</>}
                            </button>
                          ) : (
                            <span className="text-xs text-anthracite/50">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "support" && (
              <div className="grid gap-8 lg:grid-cols-2" data-testid="portal-support">
                <div>
                  <div className="rounded-2xl border border-gold/40 bg-gradient-to-br from-navy to-night p-7 text-white">
                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-gold-light"><Crown size={14} /> Support prioritaire</span>
                    <h3 className="mt-3 font-display text-2xl">Une équipe dédiée, une réponse rapide</h3>
                    <p className="mt-2 text-white/70 font-body text-sm">En tant que client, vos demandes sont traitées en priorité par un interlocuteur unique.</p>
                  </div>
                  <form onSubmit={submitTicket} className="mt-6 rounded-2xl border border-navy/10 bg-white p-7">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="subject" className="text-navy">Objet</Label>
                        <Input id="subject" data-testid="ticket-subject" value={ticket.subject} onChange={(e) => setTicket({ ...ticket, subject: e.target.value })} placeholder="Ex : Question sur mon dossier BP-2025-014" className="mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="msg" className="text-navy">Message</Label>
                        <Textarea id="msg" data-testid="ticket-message" rows={4} value={ticket.message} onChange={(e) => setTicket({ ...ticket, message: e.target.value })} placeholder="Décrivez votre demande…" className="mt-2" />
                      </div>
                    </div>
                    <button type="submit" disabled={sending} data-testid="ticket-submit" className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-6 py-3 font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60">
                      {sending ? <Loader2 size={16} className="animate-spin" /> : <>Envoyer la demande <Send size={15} /></>}
                    </button>
                  </form>
                </div>
                <div>
                  <h4 className="font-display text-lg text-navy mb-4">Vos demandes</h4>
                  {tickets.length === 0 ? (
                    <p className="text-anthracite/60 font-body text-sm">Aucune demande pour le moment.</p>
                  ) : (
                    <div className="space-y-3">
                      {tickets.map((t) => (
                        <div key={t.id} className="rounded-xl border border-navy/10 bg-white p-5">
                          <div className="flex items-center justify-between">
                            <p className="font-display text-navy">{t.subject}</p>
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusColor(t.status)}`}>{t.status}</span>
                          </div>
                          <p className="mt-2 text-sm text-anthracite font-body">{t.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </section>
  );
}
